import { Component, Input, ViewChild, OnChanges, inject } from '@angular/core';
import { formatDate } from '@angular/common';
import { AppConfig } from "app/app.config";
import {
  dataType,
  NWMTableComponent,
  optionsColumnType,
  optionsType
} from "app/components/Controls/NWMTable/NWMTable.component";
import { dateChangeType } from "app/components/Controls/daterangepicker/daterangepicker.component";
import { styleType } from 'app/services/excel.service';
import { APIService } from 'app/services/api.service';
import { AuthenticationService } from 'app/services/auth.service';
import { DateRangePickerComponent } from '../daterangepicker/daterangepicker.component';
import { FormsModule } from '@angular/forms';
import { NWMTableComponent as NWMTableComponent_1 } from '../NWMTable/NWMTable.component';

let nextId = 0;

export type assetType = {
  AcctInception: string;
  AssetSubClass: string;
  CADMarketValue: number;
  ClosedDate: string;
  CurrentPrice: number;
  CusipNum: string;
  CusipNumFormated: string;
  HoldingId: number;
  InceptionReturn: number;
  MarketValue: number;
  ProductName: string;
  TotalUnits: number;
  YtdReturn: number;
};

type planType = {
  id: string;
  Currency?: string;
  PlanName?: string;
  title?: string;
  TargetNameID1?: number;
  TargetNameID2?: number;
};

@Component({
    selector: 'portfolio-transactions',
    templateUrl: 'portfolio-transactions.component.html',
    imports: [DateRangePickerComponent, FormsModule, NWMTableComponent_1]
})

export class PortfolioTransactionsComponent implements OnChanges {
  private api = inject(APIService);
  private auth = inject(AuthenticationService);
  private config = inject(AppConfig);

  @Input() plan?: planType;
  @Input() holding?: assetType;
  @Input() inceptionDate?: string;
  @ViewChild('NWMTable') NWMTable?: NWMTableComponent;

  public dateRanges = ['ytd', '180', '60', '30'];
  public dateFrom = '';
  public dateTo = '';
  public valuationDate = '';
  public id = nextId++;

  public DepositsWithdrawalsOnly = false;
  public defaultRange = 'ytd';
  public extraCols = [
    { title: 'Unit Balance', field: 'RunningShareTotal', numberFormat: 2, class:"text-right", notSortable: true},
  ];
  public mainCols: optionsColumnType[] = [
    { title: 'Account', field: 'AccountNumber' },
    { title: 'Investment', field: 'HoldingName' },
    { title: "Trade\r\nDate", field: 'TradeDate', dateFormat: 'mediumDate', class:"text-right pre-wrap"},
    { title: "Settlement\r\nDate", field: 'SettlementDate', dateFormat: 'mediumDate', class:"text-right pre-wrap"},
    { title: 'Description', field: 'TransactionDescription' },
    { title: 'Units', field: 'Shares', numberFormat: 3, class:"text-right" },
    { title: 'Price ($)', field: 'SharePrice', numberFormat: 2, class:"text-right" },
    { title: 'Amount ($)', field: 'Amount', numberFormat: 2, class:"text-right" },
  ];
  public tableOptions: optionsType = {
    tableSubscriptor: (callback, errorCallback) => this.api.post<dataType>(this.config.portfolio.contributionsWithdrawalsUrl, this.tableOptions!.request).subscribe(
      callback,
      errorCallback,
    ),
    exportSubscriptor: (callback, errorCallback) => this.api.post<dataType>(this.config.portfolio.contributionsWithdrawalsUrl, {
          ...this.tableOptions!.request,
          Page: 1,
          PageSize: 10000,
        }
      ).subscribe(
      callback,
      errorCallback,
    ),
    columns: [],
    tooltipField: '',
    request : {
      "Page": 1,
      "PageSize": 20,
      "SortFields": ["SettlementDate"],
      "SortDirections": ["Descending"],
      "FilterProperty": [
        "ValuationDate",
        "TargetNameID1",
        "TargetNameID2",
        "PlanName",
        "DateFrom",
        "DateTo",
        "Currency",
        "DepositWithdrwl",
        ],
      "FilterOperator": [
        "Equals",
        "Equals",
        "Equals",
        "Equals",
        "Equals",
        "Equals",
        "Equals",
        "Equals",
        "Equals",
        ],
      "Filtertype": [],
      "FilterValue": []
    }
  }

  ngOnChanges() {
    this.defaultRange = '';
    setTimeout(()=>{ // force change detection
      this.defaultRange = 'ytd';
    });

    this.DepositsWithdrawalsOnly = false;
  }

  onDatesChange(dates: dateChangeType) {
    this.dateFrom = dates.from;
    this.dateTo = dates.to;
    this.requestData();
  }
  DWChange() {
    setTimeout(()=>this.requestData(),1);
  }

  requestData() {
    this.valuationDate = this.auth.getCurrentValuationDate();

    this.tableOptions.request.Page = 1;
    this.resetFilters();
    setTimeout(()=>{ // force change detection - otherwise it was not pushing data to NWMTable
      this.addFilter("ValuationDate", this.valuationDate);
      this.addFilter("TargetNameID1", this.plan!.TargetNameID1 as number);

      if (this.plan!.TargetNameID2)
        this.addFilter("TargetNameID2", this.plan!.TargetNameID2);
      if (this.plan!.PlanName)
        this.addFilter("PlanName", this.plan!.PlanName);

      this.addFilter("DateFrom", this.dateFrom);
      this.addFilter("DateTo",   this.dateTo);
      this.addFilter("Currency", this.plan!.Currency as string);

      if (this.DepositsWithdrawalsOnly) {
        this.addFilter("DepositWithdrwl", 1);
      }
      this.tableOptions.columns = [...this.mainCols];
      if (this.holding) {
        this.tableOptions.columns[0].notSortable = true;
        this.tableOptions.columns[1].notSortable = true;
        if (!this.DepositsWithdrawalsOnly) {
          this.extraCols[0].title = (this.holding.AssetSubClass == 'Cash' && this.holding.CusipNumFormated === '') ? 'Cash Balance ($)' : 'Unit Balance';
          this.tableOptions.columns.push(...this.extraCols);
        }
        this.addFilter("HoldingID", this.holding.HoldingId.toString());
      }
    }, 0);
  }

  resetFilters() {
    this.tableOptions.request.FilterProperty = [];
    this.tableOptions.request.FilterOperator = [];
    this.tableOptions.request.FilterValue = [];
  }

  addFilter(fp: string, fv: string | number, fo = "Equals") {
    this.tableOptions.request.FilterProperty.push(fp);
    this.tableOptions.request.FilterOperator.push(fo);
    this.tableOptions.request.FilterValue.push(fv);
  }

  export(rows: (string | number)[][] = [], styles: styleType[]  = []) {
    rows.push([formatDate(this.dateFrom, 'MMM d, yyyy', 'en-US') + ' - ' + formatDate(this.dateTo, 'MMM d, yyyy', 'en-US')]);
    this.NWMTable!.export('Transactions.xlsx', 'Sheet 1', rows, styles);
  }
}
